import React from 'react';
import ENDPOINT from '../../constants/endpoints';
import {CONNECT_STATUS} from '../../constants/profile';
import {FirmActions} from '../../helpers/actions/firm';
import {NetworkActions} from '../../helpers/actions/network';
import {SearchActions} from '../../helpers/actions/search';
import FileHelper from '../../helpers/file';
import LinkBuilder from '../../helpers/link-builder';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import ConfirmDialog from '../../scenes/Dialog/components/ConfirmDialog';
import FormDialog from '../../scenes/Dialog/components/FormDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import ShortlistActivityDialog from '../../scenes/Dialog/components/ShortlistActivityDialog';
import ProfileAddFilesForm from '../../scenes/Form/components/ProfileAddFilesForm';
import ProfileApplyCandidateToPositionForm from '../../scenes/Form/components/ProfileApplyCandidateToPositionForm';
import ProfileSendReviewForm from '../../scenes/Form/components/ProfileSendReviewForm';
import ShortlistAddCandidatesForm from '../../scenes/Form/components/ShortlistAddCandidatesForm';


const action = {
  connect: (api, id, { state, setState }) => {
    const action = new NetworkActions(api);
    return action.connect({ id }).then(() =>
      setState({
        ...state,
        profile: {
          ...state.profile,
          ConnectStatus: CONNECT_STATUS.REQUIRE_APPROVE,
        },
      })
    );
  },

  disconnect: (api, id, { state, setState }) => {
    const action = new NetworkActions(api);
    return new Promise(resolve => {
      ConfirmDialog({
        dismissWhenClose: true,
        bodyRender: `Are you sure that you want to delete ${state.profile.FullName} from your connections list?`,
        textSuccessBtn: 'Yes',
        textCancelBtn: 'No',
      }).then(() =>
        action.disconnect({ id }).then(() => {
          setState({
            ...state,
            profile: {
              ...state.profile,
              ConnectStatus: CONNECT_STATUS.NOT_CONNECTED,
            },
          });
          resolve();
        })
      );
    });
  },

  accept: (api, id, { state, setState }) => {
    const action = new NetworkActions(api);
    return action.accept({ token: state.profile.ConnectActionToken }).then(() =>
      setState({
        ...state,
        profile: {
          ...state.profile,
          ConnectStatus: CONNECT_STATUS.CONNECTED,
          ConnectActionToken: null,
        },
      })
    );
  },

  reject: (api, id, { state, setState }) => {
    const action = new NetworkActions(api);
    return action.reject({ token: state.profile.ConnectActionToken }).then(() =>
      setState({
        ...state,
        profile: {
          ...state.profile,
          ConnectStatus: CONNECT_STATUS.NOT_CONNECTED,
          ConnectActionToken: null,
        },
      })
    );
  },

  sendMessage: (api, history, user, { id, FullName, enableTemplates }) => {
    const action = new NetworkActions();
    action.sendMessage(id, FullName, enableTemplates);
  },

  downloadResume: (id, revision = null) => {
    const url = LinkBuilder.getCandidateResumeLink(id, revision);
    FileHelper.downloadByUrlWithChecking(
      url,
      { act: 'downloadResume', page: 0 },
      {
        companyRight: 'You do not have permission to download resumes.',
        exist: 'File not found.',
      }
    );
  },

  downloadFile: ({ CandidateID, FileID }) => {
    const url = LinkBuilder.getFileLink(FileID, CandidateID);

    FileHelper.downloadByUrlWithChecking(
      url,
      { act: 'downloadFiles', typeId: FileID, candidateId: CandidateID },
      {
        companyRight: 'You do not have permission to download files.',
        exist: 'Candidate has not yet uploaded files.',
      }
    );
  },

  downloadAllFiles: ({ CandidateID, FileTypeID }) => {
    const url = LinkBuilder.getAllFileLink(FileTypeID, CandidateID);

    FileHelper.downloadByUrlWithChecking(
      url,
      { act: 'downloadFiles', typeId: FileTypeID, candidateId: CandidateID },
      {
        companyRight: 'You do not have permission to download files.',
        exist: 'Candidate has not yet uploaded files.',
      }
    );
  },

  beforeSetFirmRating: (api, user) => {
    const action = new FirmActions(api, user);
    return action.checkRatingUpdate(user);
  },

  setFirmRating: (api, user, { id, mark }) => {
    const action = new FirmActions(api, user);
    return action.setRating({ id, mark });
  },

  rate: (api, user, id, { state, setState, callback }) => {
    const candidate = {
      CandidateID: id,
      FullName: state.profile.FullName,
      PictureUrl: state.profile.PictureUrl,
      FirmName: state.profile.Positions[0].FirmName,
      OfficeLocationName: state.profile.Positions[0].OfficeLocationName,
      RoleID: state.profile.Positions[0].RoleID,
      SchoolName: state.profile.SchoolName,
      currentSchoolName: state.profile.SchoolName,
    };

    const action = new SearchActions(api, user);
    return action.rate(candidate).then(({ Rating, Spoke, Met, Notes }) => {
      setState(prevState => ({
        ...prevState,
        profile: { ...state.profile, Rated: Rating || Spoke || Met || Notes },
      }));
      callback();
    });
  },

  tag: (api, user, id, { state, callback }) => {
    const candidate = {
      CandidateID: id,
      FullName: state.profile.FullName,
      PictureUrl: state.profile.PictureUrl,
      FirmName: state.profile.Positions[0].FirmName,
      OfficeLocationName: state.profile.Positions[0].OfficeLocationName,
      RoleID: state.profile.Positions[0].RoleID,
      SchoolName: state.profile.SchoolName,
      currentSchoolName: state.profile.SchoolName,
    };

    const action = new SearchActions(api, user);
    return action.tag(candidate).then(callback);
  },

  apply: (id, callback) => {
    FormDialog({
      title: 'Apply Candidate to Position',
      compact: true,
      modeless: false,
      params: { id },
      form: (actions, params) => (
        <ProfileApplyCandidateToPositionForm
          actions={actions}
          params={params}
        />
      ),
    }).then(
      () => callback(),
      response => ResponseErrorDialog({ response })
    );
  },

  addFiles: (id, callback) => {
    FormDialog({
      title: 'Add Files',
      popupStyles: {
        maxWidth: '768px',
      },
      compact: true,
      modeless: false,
      params: { id },
      form: (actions, params) => (
        <ProfileAddFilesForm actions={actions} params={params} />
      ),
    }).then(
      () => {
        callback();
        BasicDialog({ bodyRender: 'Files were uploaded.' });
      },
      response => ResponseErrorDialog({ response })
    );
  },

  addToShortlist: (id, name, history) => {
    FormDialog({
      title: 'Add to Shortlist',
      popupStyles: {
        maxWidth: '550px',
      },
      compact: true,
      modeless: false,
      params: { ids: [id], showActivityAfterSave: true },
      form: (actions, params) => (
        <ShortlistAddCandidatesForm actions={actions} params={params} />
      ),
    }).then(
      ({ id: shortListId, activity }) => {
        if (activity && activity.length) {
          ShortlistActivityDialog({ CandidateName: name, data: activity }).then(
            () => {
              history.push(LinkBuilder.getShortlistLink(shortListId));
            }
          );
        } else {
          ConfirmDialog({
            bodyRender: `${name} has been added to Shortlist`,
            textCancelBtn: 'Stay here',
            textSuccessBtn: 'Go to Shortlist',
            dismissWhenClose: true,
          }).then(() => {
            history.push(LinkBuilder.getShortlistLink(shortListId));
          });
        }
      },
      response => ResponseErrorDialog({ response })
    );
  },

  sendInterviewQuestionsEmail: (state, api, id, { setState, callback }) =>
    ConfirmDialog({
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      bodyRender:
        'Are you sure you want to send interview questions to this candidate?',
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.CANDIDATE_NOTIFY_INTERVIEW_QUESTIONS,
        { id },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: response => {
            setState({
              ...state,
              profile: {
                ...state.profile,
                ...response,
              },
            });
            callback();
          },
        }
      );
    }),

  sendReview: (id, callback) => {
    FormDialog({
      title: 'Send Review',
      compact: true,
      modeless: false,
      params: { id },
      form: (actions, params) => (
        <ProfileSendReviewForm actions={actions} params={params} />
      ),
    }).then(
      () => callback(),
      response => ResponseErrorDialog({ response })
    );
  },

  sendUpdateResumeEmail: (api, id, callback) => {
    ConfirmDialog({
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      bodyRender:
        'Are you sure you want to send Update Resume Request to this Candidate?',
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.CANDIDATE_NOTIFY_UPDATE_RESUME,
        { id },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => callback(),
        }
      );
    });
  },

  sendGATEmail: (api, id, callback) => {
    ConfirmDialog({
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      bodyRender:
        'Are you sure you want to administer the GoBuyside Aptitude Test to this Candidate?',
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.CANDIDATE_NOTIFY_GAT,
        { id },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => callback(),
        }
      );
    });
  },

  sendJob: (id, callback) => {
    FormDialog({
      title: 'Send Job',
      compact: true,
      modeless: false,
      params: { id, isSendJobButton: true, successBtnText: 'Send Job' },
      form: (actions, params) => (
        <ProfileApplyCandidateToPositionForm
          actions={actions}
          params={params}
        />
      ),
    }).then(
      () => callback(),
      response => ResponseErrorDialog({ response })
    );
  },

  lockProfile: (id, lock, api, callback) => {
    ConfirmDialog({
      dismissWhenClose: true,
      bodyRender: lock
        ? 'Are you sure you want to lock this profile?'
        : 'Are you sure you want to unlock this profile?',
      textSuccessBtn: 'Yes',
      textCancelBtn: 'Cancel',
    }).then(
      () => {
        api.post(
          ENDPOINT.ACTION.CANDIDATE_LOCK_TOGGLE,
          { id },
          {
            handleError: response => ResponseErrorDialog({ response }),
            handleSuccess: response => callback(response),
          }
        );
      },
      response => ResponseErrorDialog({ response })
    );
  },
};

export default action;
